import React, { useEffect, useState } from 'react';
import { useFetchOrdersDeliveredAtForReport } from 'hooks/useFetchOrdersDeliveredAtForReport';
import { OrderStatus, Region } from 'types/orderTypes';
import { BillingCourier } from 'components/OrderListTable/BillingCourier/BillingCourier';
import { Loader } from 'components/Loader/Loader';
import Input from 'components/inputs/Input';
import Dropdown from 'components/dropdown/Dropdown';
import {
  CalendarFetchByPeriods,
  getEndDate,
  getStartDate,
} from 'components/OrderListTable/CalendarFetch/CalendarFetchByPeriods';
import { ReqEnum } from 'const/requestStatus';
import { groupBy } from 'components/OrderListTable/utils/groupBy';
import './MyBooking.scss';
import { CourierInvoice } from '../../types/reportTypes';
import { useFetchShiftByRegion } from '../../hooks/useFetchShift';
import { Helmet } from 'react-helmet';

export function MyBooking({ region }: { region: Region }): JSX.Element {
  const [startDate, setStartDate] = useState(getStartDate());
  const [endDate, setEndDate] = useState(getEndDate());

  const fetchValues = values => {
    const { fetchStartDate, fetchEndDate } = values;
    setStartDate(fetchStartDate);
    setEndDate(fetchEndDate);
  };

  const [courierInvoiceList, fetchStatus] = useFetchOrdersDeliveredAtForReport(
    startDate,
    endDate
  );

  const [courierListBookedFetched, fetchStatusShift] = useFetchShiftByRegion(
    startDate,
    endDate,
    region
  );

  const currentUserCourierInvoice: CourierInvoice = courierInvoiceList[0];

  return (
    <>
      <Helmet>
        <title>My Booking</title>
      </Helmet>
      <div className="u-screen-only">
        <CalendarFetchByPeriods
          fetchValues={fetchValues}
          givenStartDate={startDate}
          givenEndDate={endDate}
        />
        {fetchStatus === ReqEnum.PENDING && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Loader type="spinner" size="large" />
          </div>
        )}
      </div>

      <React.Fragment>
        <BillingCourier
          courierInvoiceList={currentUserCourierInvoice}
          fetchStatus={fetchStatus}
          startDate={startDate}
          endDate={endDate}
          courierListBookedFetched={courierListBookedFetched}
          fetchStatusShift={fetchStatusShift}
          region={region}
        />
      </React.Fragment>
    </>
  );
}

function onlyUnique(value, index, self) {
  return self.indexOf(value) === index;
}

function findUniqueCourierNameListFromOrders(
  ordersByCourierNameArray,
  searchText
) {
  if (ordersByCourierNameArray) {
    return ordersByCourierNameArray
      .map((obj, index) => obj[0])
      .filter(user => user != null)
      .filter(user => user !== 'null')
      .filter(onlyUnique)
      .filter(name => name.toLowerCase().includes(searchText.toLowerCase()))
      .sort((a, b) => a.localeCompare(b));
  } else {
    return ['loading...'];
  }
}

function BillingCourierById({
  fetchStatus,
  showInvoiceBySelectedCourier,
  courierId,
  startDate,
  endDate,
  courierListBookedFetched,
  fetchStatusShift,
  region,
}: {
  fetchStatus;
  showInvoiceBySelectedCourier: CourierInvoice;
  courierId: number;
  startDate;
  endDate;
  courierListBookedFetched;
  fetchStatusShift;
  region: Region;
}) {
  return (
    <>
      {fetchStatus === ReqEnum.PENDING && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Loader type="spinner" size="large" />
        </div>
      )}
      {courierId === null ? (
        <div>
          {' '}
          This courier dont have a courier Id. Talk to the programmers or add it
          yourself
        </div>
      ) : (
        <BillingCourier
          courierInvoiceList={showInvoiceBySelectedCourier}
          fetchStatus={fetchStatus}
          startDate={startDate}
          endDate={endDate}
          courierListBookedFetched={courierListBookedFetched}
          fetchStatusShift={fetchStatusShift}
          region={region}
        />
      )}
    </>
  );
}

function makeOrdersByCourierNameArray(orders) {
  const ordersByCourierName = groupBy(orders, 'courierName');
  const ordersByCourierNameArray: any = Object.entries(ordersByCourierName);
  return ordersByCourierNameArray;
}

function makeShowOrders(ordersByCourierNameArray, courierName) {
  if (
    courierName.length > 0 &&
    ordersByCourierNameArray &&
    ordersByCourierNameArray.length
  ) {
    const showOrders = ordersByCourierNameArray
      .filter(obj => obj[0] === courierName)
      .map(obj => obj[1])[0]
      .filter(order => order.status !== OrderStatus.Canceled);
    return showOrders;
  } else {
    return [];
  }
}

function isCourierNameInCourierNameList(
  ordersByCourierNameArray,
  selectedCourierName
) {
  const arrayMatchingSelectedCourierName = ordersByCourierNameArray.filter(
    obj => obj.indexOf(selectedCourierName) === 0
  );

  return arrayMatchingSelectedCourierName.length ? true : false;
}

export function MyBookingMultipleCouriers({
  region,
}: {
  region: Region;
}): JSX.Element {
  const [startDate, setStartDate] = useState(getStartDate());
  const [endDate, setEndDate] = useState(getEndDate());
  const [searchText, setSearchText] = useState<string>('');

  const [courierInvoiceList, fetchStatus] = useFetchOrdersDeliveredAtForReport(
    startDate,
    endDate
  );

  const [courierListBookedFetched, fetchStatusShift] = useFetchShiftByRegion(
    startDate,
    endDate,
    region
  );

  const [selectedCourierName, setSelectedCourierName] = useState<string>('');
  const [selectedCourierId, setSelectedCourierId] = useState<number>(0);

  const [showInvoiceBySelectedCourier, setShowInvoiceBySelectedCourier] =
    useState();

  const [uniqueCourierNameListOrdersList, setUniqueCourierNameListOrdersList] =
    useState<[]>();

  useEffect(() => {
    const updateData = (
      courierInvoiceList,
      selectedCourierName,
      fetchStatus,
      searchText
    ) => {
      if (fetchStatus === ReqEnum.SUCCESS) {
        const ordersByCourierNameArray =
          makeOrdersByCourierNameArray(courierInvoiceList);
        setUniqueCourierNameListOrdersList(
          findUniqueCourierNameListFromOrders(
            ordersByCourierNameArray,
            searchText
          )
        );
        if (
          isCourierNameInCourierNameList(
            ordersByCourierNameArray,
            selectedCourierName
          )
        ) {
          const showInvoiceBySelectedCourier = makeShowOrders(
            ordersByCourierNameArray,
            selectedCourierName
          );
          setShowInvoiceBySelectedCourier(showInvoiceBySelectedCourier);
          if (showInvoiceBySelectedCourier.length) {
            setSelectedCourierId(showInvoiceBySelectedCourier.courierId);
          } else {
            setSelectedCourierId(null);
          }
        } else {
          setSelectedCourierId(0);
          setSelectedCourierName('');
        }
      }
    };
    updateData(
      courierInvoiceList,
      selectedCourierName,
      fetchStatus,
      searchText
    );
  }, [selectedCourierName, fetchStatus, searchText, courierInvoiceList]);

  const fetchValues = values => {
    const { fetchStartDate, fetchEndDate } = values;
    setStartDate(fetchStartDate);
    setEndDate(fetchEndDate);
  };

  const chooseCourier = (courierName: string) => {
    setSelectedCourierName(courierName);
  };

  return (
    <>
      <div className="chooseCourierInvoiceRow">
        <Input
          className="searchInput"
          placeholder="Filter courier list.."
          onChange={event => setSearchText(event.target.value)}
          value={searchText}
          autoFocus={searchText !== ''}
          onRef={input => searchText !== '' && input && input.focus()}
          disabled={fetchStatus !== ReqEnum.SUCCESS}
        />
        <Dropdown
          placeholder={
            selectedCourierName
              ? selectedCourierName
              : 'Show invoice by courier'
          }
          onChange={courierName => chooseCourier(courierName)}
          names={
            uniqueCourierNameListOrdersList
              ? uniqueCourierNameListOrdersList.length > 0
                ? uniqueCourierNameListOrdersList
                : ['Empty for this period']
              : ['Loading ...']
          }
        />
      </div>

      <CalendarFetchByPeriods
        fetchValues={fetchValues}
        givenStartDate={startDate}
        givenEndDate={endDate}
      />
      {selectedCourierName.length > 0 && (
        <BillingCourierById
          courierId={selectedCourierId}
          fetchStatus={fetchStatus}
          showInvoiceBySelectedCourier={showInvoiceBySelectedCourier}
          startDate={startDate}
          endDate={endDate}
          courierListBookedFetched={courierListBookedFetched}
          fetchStatusShift={fetchStatusShift}
          region={region}
        />
      )}
    </>
  );
}
